import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getGoldHistory } from "@/lib/gold-history.functions";
import { formatMoney, type Country } from "@/lib/gold-data";

export const historyQueryOptions = (country: Country) =>
  queryOptions({
    queryKey: ["gold-history", country],
    queryFn: () => getGoldHistory({ data: { country } }),
    staleTime: 10 * 60 * 1000,
  });

export function GoldChart({
  country,
  displayCurrency,
  transform,
  unitLabel: unitLabelProp,
}: {
  country: Country;
  /** Override the currency the values are rendered in (e.g. "AED"). */
  displayCurrency?: string;
  /** Convert each stored value into the display currency/unit. */
  transform?: (value: number) => number;
  /** Override the unit wording shown in the sub-heading. */
  unitLabel?: string;
}) {
  const { data: raw } = useSuspenseQuery(historyQueryOptions(country));
  const currency = displayCurrency ?? (country === "pakistan" ? "PKR" : "INR");
  const unitLabel = unitLabelProp ?? (country === "pakistan" ? "per tola" : "per 10g");
  const data = transform
    ? {
        ...raw,
        points: raw.points.map((p) => ({
          ...p,
          price24k: transform(p.price24k),
          price22k: transform(p.price22k),
        })),
      }
    : raw;

  return (
    <section className="mx-auto max-w-6xl px-4 pb-12">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Last 6 months — 24K gold trend</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Daily 24K &amp; 22K rate in {currency} ({unitLabel}). Rolling 6-month window — a new point is added every day and the oldest drops off.
      </p>
      <div className="rounded-xl border border-border bg-card p-4 min-h-[320px]">
        {data.points.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center h-72 flex items-center justify-center">
            Today's rate is being recorded. Tomorrow you'll see a 2-day trend, growing daily up to 3 months.
          </div>
        ) : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.points} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  tickFormatter={(d: string) =>
                    new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  }
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  domain={["auto", "auto"]}
                  width={70}
                  tickFormatter={(v: number) => Math.round(v).toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(d) => new Date(d as string).toDateString()}
                  formatter={(value: number, name) => [
                    displayCurrency
                      ? `${displayCurrency} ${Math.round(value).toLocaleString("en-AE")}`
                      : formatMoney(value, currency as "PKR" | "INR"),
                    name,
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="price24k"
                  name="24K"
                  stroke="#d4a017"
                  strokeWidth={2}
                  dot={data.points.length < 30}
                />
                <Line
                  type="monotone"
                  dataKey="price22k"
                  name="22K"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}